<template>
    <div :dir="isRtl ? 'rtl' : 'ltr'" :class="{ 'text-end': isRtl, 'text-start': !isRtl }">
        <div class="bg-body-light">
            <div class="content content-full">
                <div class="d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center py-2">
                    <div class="flex-grow-1">
                        <h1 class="h3 fw-bold mb-1">
                            {{ $t('title_all_accounts') }}
                        </h1>
                        <h2 class="fs-base lh-base fw-medium text-muted mb-0">
                            {{ companyTitle }}
                        </h2>
                    </div>

                    <AddAccountModal v-model:visible="showModal" @account-created="fetchAccounts" />

                </div>
            </div>
        </div>
        <div class="content">
            <div class="row" v-if="accountTypes.length">
                <div class="col-md-6 col-xl-3" v-for="type in accountTypes" :key="type._id">
                    <a class="block block-rounded block-link-shadow" :class="'bg-' + type.color"
                        href="javascript:void(0)">
                        <div class="block-content block-content-full d-flex align-items-center justify-content-between">
                            <div>
                                <i :class="'fa-2x ' + getIconForType(type._id) + ' text-white-50'"></i>
                            </div>
                            <dl class="ms-3 text-end mb-0">
                                <dt class="text-white h3 fw-extrabold mb-0">
                                    <!-- Display the balance for each type -->
                                    
                                    <span v-html="formatWithSmallDecimals(getBalanceForType(type._id))"></span>
                                </dt>
                                <dd class="text-white fs-sm fw-medium text-muted mb-0">
                                    {{ type.title_ar }}
                                </dd>
                            </dl>
                        </div>
                    </a>
                </div>
            </div>

            <AccountsTable :accounts="localAccounts" :accountTypes="accountTypes" @filters-updated="fetchAccounts" />
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  formatNumberShort,
  formatNumberWithCommas,
  formatDate
} from '@/utils' // adjust the path to match your project
import axios from 'axios'
import AccountsTable from './components/AccountsTable.vue'
import AddAccountModal from './components/AddAccountModal.vue'
import swal from '../../../utils/swal'

const { locale } = useI18n()

const isRtl = computed(() => locale.value === 'ar')

const props = defineProps({
    accounts: Array,
    accountStatus: Array,
})

const accountTypes = ref([]);

const fetchingAccounts = ref(false)
let showModal = ref(false)

const localAccounts = ref([...props.accounts ?? []])
const localBalances = ref([])
const total = ref(0)
const iconMapping = {
      '1': 'fa-solid fa-coins',              // For type_id = '1'
      '2': 'fa-solid fa-arrows-down-to-people',      // For type_id = '2'
      '3': 'fa-solid fa-chart-column',      // For type_id = '3'
      '4': 'fa-solid fa-money-bill-trend-up',     // For type_id = '4'
      '5': 'fa-solid fa-cart-shopping',                   // For type_id = '5'
      // Add more icons as needed
    };

    const getIconForType = (typeId) => {
      return iconMapping[typeId] || 'fa-question-circle'; // Default icon
    };

function getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function getBalanceForType(typeId) {
    const balance = localBalances.value.find(balance => balance.type_id === typeId);
    return balance ? balance.total_balance : 0; // Return balance or 0 if not found
}

function formatWithSmallDecimals(value) {
  const formatted = formatNumberWithCommas(value)
  const [intPart, decimalPart] = formatted.split('.')

  if (decimalPart) {
    return `${intPart}.<small style="font-size: 0.7em; font-weight: normal;">${decimalPart}</small>`
  } else {
    return intPart
  }
}

function cleanFilters(filters) {
    const cleaned = {}
    for (const key in filters) {
        const value = filters[key]
        if (
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
        ) continue
        cleaned[key] = value
    }
    return cleaned
}

function fetchAccounts(filters = {}, page = 1, perPage = 50) {
    if (window.One) window.One.layout('header_loader_on')
    fetchingAccounts.value = true

    const cleanedFilters = cleanFilters(filters)

    axios.post('/accounts/get-all-accounts', {
        ...cleanedFilters,
        page,
        perPage
    }, {
        headers: {
            'X-CSRF-TOKEN': getCSRFToken(),
            'Content-Type': 'application/json'
        }
    })
        .then(response => {
            console.log('Response:', response)
            const { accounts, balances, total: totalCount } = response.data
            localAccounts.value = accounts
            localBalances.value = balances;
            total.value = totalCount
            console.log('Accounts fetched from parent:', accounts)
        })
        .catch(error => {
            console.error('Error fetching accounts:', error)
        })
        .finally(() => {
            fetchingAccounts.value = false
            if (window.One) window.One.layout('header_loader_off')
        })
}

const getAccountTypes = async () => {
    try {
        const response = await axios.get('/accounts/get-account-types');
        accountTypes.value = response.data;
    } catch (error) {
        console.error('Error fetching account types:', error);
        swal.error(this.$t('message_general_error'));
    }
};

onMounted(() => {
    getAccountTypes()
    fetchAccounts()
    getIconForType()
})
</script>

<style scoped>
@media (min-width: 1200px) {
  .col-xl-custom-5 {
    width: 20%;
    flex: 0 0 20%;
  }
}
</style>